/**
 * Pixel images for the tile editor: RGBA bytes, rows from the top. Colours are packed as
 * 0xRRGGBBAA (unsigned); every fully transparent pixel is 0.
 */

export interface Img {
  w: number;
  h: number;
  d: Uint8ClampedArray;
}

export const CLEAR = 0;

export function blank(w: number, h = w): Img {
  return { w, h, d: new Uint8ClampedArray(w * h * 4) };
}

export function cloneImg(a: Img): Img {
  return { w: a.w, h: a.h, d: new Uint8ClampedArray(a.d) };
}

export function sameImg(a: Img, b: Img): boolean {
  if (a.w !== b.w || a.h !== b.h) return false;
  for (let i = 0; i < a.d.length; i++) if (a.d[i] !== b.d[i]) return false;
  return true;
}

export function getPx(a: Img, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= a.w || y >= a.h) return CLEAR;
  const i = (y * a.w + x) * 4;
  if (!a.d[i + 3]) return CLEAR;
  return ((a.d[i] << 24) | (a.d[i + 1] << 16) | (a.d[i + 2] << 8) | a.d[i + 3]) >>> 0;
}

export function setPx(a: Img, x: number, y: number, c: number) {
  if (x < 0 || y < 0 || x >= a.w || y >= a.h) return;
  const i = (y * a.w + x) * 4;
  const al = c & 255;
  a.d[i] = al ? c >>> 24 : 0;
  a.d[i + 1] = al ? (c >>> 16) & 255 : 0;
  a.d[i + 2] = al ? (c >>> 8) & 255 : 0;
  a.d[i + 3] = al;
}

export const alphaOf = (c: number) => c & 255;

export function hexOf(c: number): string {
  if (!(c & 255)) return 'transparent';
  const rgb = (c >>> 8).toString(16).padStart(6, '0');
  return (c & 255) === 255 ? `#${rgb}` : `#${rgb}${(c & 255).toString(16).padStart(2, '0')}`;
}

export function parseHex(s: string): number {
  const m = /^#?([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(s.trim());
  if (!m) return CLEAR;
  return ((parseInt(m[1], 16) << 8) | (m[2] ? parseInt(m[2], 16) : 255)) >>> 0;
}

export function cssOf(c: number): string {
  if (!(c & 255)) return 'transparent';
  return `rgba(${c >>> 24},${(c >>> 16) & 255},${(c >>> 8) & 255},${((c & 255) / 255).toFixed(3)})`;
}

const rgb = (c: number): [number, number, number] => [c >>> 24, (c >>> 16) & 255, (c >>> 8) & 255];

export function colorDist(a: number, b: number): number {
  const [r1, g1, b1] = rgb(a), [r2, g2, b2] = rgb(b);
  // weighted RGB distance: close enough to perceived difference for picking palette neighbours
  const rm = (r1 + r2) / 2;
  return Math.sqrt((2 + rm / 256) * (r1 - r2) ** 2 + 4 * (g1 - g2) ** 2 + (2 + (255 - rm) / 256) * (b1 - b2) ** 2) + Math.abs((a & 255) - (b & 255));
}

export function nearestColor(c: number, palette: number[]): number {
  if (!(c & 255) || !palette.length) return c;
  let best = palette[0], bd = Infinity;
  for (const p of palette) {
    const d = colorDist(c, p);
    if (d < bd) [bd, best] = [d, p];
  }
  return best;
}

/** Hue-sorted colour order: greys first by lightness, then by hue band and lightness. */
export function sortColors(cs: number[]): number[] {
  const key = (c: number) => {
    const [r, g, b] = rgb(c).map((v) => v / 255);
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    const l = (mx + mn) / 2;
    const s = mx - mn;
    if (s < 0.08) return [0, 0, l];
    let hh = mx === r ? ((g - b) / s) % 6 : mx === g ? (b - r) / s + 2 : (r - g) / s + 4;
    if (hh < 0) hh += 6;
    return [1, Math.floor(hh * 2), l];
  };
  return [...cs].sort((a, b) => {
    const ka = key(a), kb = key(b);
    return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
  });
}

/** Colours of images by use count (most used first), transparent left out. */
export function colorCounts(imgs: Img[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const a of imgs)
    for (let y = 0; y < a.h; y++)
      for (let x = 0; x < a.w; x++) {
        const c = getPx(a, x, y);
        if (c) m.set(c, (m.get(c) ?? 0) + 1);
      }
  return m;
}

export function paletteOf(imgs: Img[], max = 64, minCount = 1): number[] {
  const m = colorCounts(imgs);
  const top = [...m.entries()].filter(([, n]) => n >= minCount).sort((a, b) => b[1] - a[1]).slice(0, max).map(([c]) => c);
  return sortColors(top);
}

// ------------------------------------------------------------------ drawing primitives

export function linePoints(x0: number, y0: number, x1: number, y1: number): [number, number][] {
  const out: [number, number][] = [];
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy, x = x0, y = y0;
  for (;;) {
    out.push([x, y]);
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
  return out;
}

export function rectPoints(x0: number, y0: number, x1: number, y1: number, filled: boolean): [number, number][] {
  const ax = Math.min(x0, x1), bx = Math.max(x0, x1), ay = Math.min(y0, y1), by = Math.max(y0, y1);
  const out: [number, number][] = [];
  for (let y = ay; y <= by; y++) for (let x = ax; x <= bx; x++) if (filled || x === ax || x === bx || y === ay || y === by) out.push([x, y]);
  return out;
}

/** Pixels a fill at (x, y) changes: the 4-connected area of the same colour, or that colour everywhere. */
export function fillPoints(a: Img, x: number, y: number, contiguous: boolean): [number, number][] {
  const target = getPx(a, x, y);
  const out: [number, number][] = [];
  if (x < 0 || y < 0 || x >= a.w || y >= a.h) return out;
  if (!contiguous) {
    for (let yy = 0; yy < a.h; yy++) for (let xx = 0; xx < a.w; xx++) if (getPx(a, xx, yy) === target) out.push([xx, yy]);
    return out;
  }
  const seen = new Uint8Array(a.w * a.h);
  const stack: [number, number][] = [[x, y]];
  while (stack.length) {
    const [px, py] = stack.pop()!;
    if (px < 0 || py < 0 || px >= a.w || py >= a.h || seen[py * a.w + px]) continue;
    seen[py * a.w + px] = 1;
    if (getPx(a, px, py) !== target) continue;
    out.push([px, py]);
    stack.push([px + 1, py], [px - 1, py], [px, py + 1], [px, py - 1]);
  }
  return out;
}

/**
 * Pixel-perfect freehand: drop the middle pixel of every L-shaped corner (the doubled pixel a 1 px
 * line gets when the pointer moves diagonally). Returns the indices into `pts` to remove.
 */
export function pixelPerfectDrops(pts: [number, number][]): Set<number> {
  const drop = new Set<number>();
  let prev = -1, mid = -1;
  for (let i = 0; i < pts.length; i++) {
    if (mid >= 0 && prev >= 0) {
      const [ax, ay] = pts[prev], [bx, by] = pts[mid], [cx, cy] = pts[i];
      const l = (ax === bx || ay === by) && (bx === cx || by === cy) && Math.abs(ax - cx) === 1 && Math.abs(ay - cy) === 1;
      if (l) {
        drop.add(mid);
        mid = i;
        continue;
      }
    }
    prev = mid;
    mid = i;
  }
  return drop;
}

// ------------------------------------------------------------------ whole-image transforms

export function mapImg(a: Img, src: (x: number, y: number) => [number, number]): Img {
  const o = blank(a.w, a.h);
  for (let y = 0; y < a.h; y++)
    for (let x = 0; x < a.w; x++) {
      const [sx, sy] = src(x, y);
      setPx(o, x, y, getPx(a, sx, sy));
    }
  return o;
}

export const flipH = (a: Img) => mapImg(a, (x, y) => [a.w - 1 - x, y]);
export const flipV = (a: Img) => mapImg(a, (x, y) => [x, a.h - 1 - y]);

/** Rotate a square image a quarter turn (clockwise on screen). */
export function rotate(a: Img, cw: boolean): Img {
  const n = a.w;
  return mapImg(a, (x, y) => (cw ? [y, n - 1 - x] : [n - 1 - y, x]));
}

export function shift(a: Img, dx: number, dy: number): Img {
  return mapImg(a, (x, y) => [(((x - dx) % a.w) + a.w) % a.w, (((y - dy) % a.h) + a.h) % a.h]);
}

/**
 * Apply Unity tile flags (bit0 flip X, bit1 flip Y, bit2 swap axes; display = M·sprite with y up) to a
 * square image, or undo them with `inverse`.
 */
export function applyFlags(a: Img, f: number, inverse = false): Img {
  if (!f) return a;
  let ma = 1, mb = 0, mc = 0, md = 1;
  if (f & 4) [ma, mb, mc, md] = [0, 1, 1, 0];
  if (f & 1) [ma, mb] = [-ma, -mb];
  if (f & 2) [mc, md] = [-mc, -md];
  const n = a.w, h = n / 2;
  return mapImg(a, (x, y) => {
    // destination pixel centre in y-up coordinates around the image centre
    const qx = x + 0.5 - h, qy = n - y - 0.5 - h;
    // M is orthogonal: its inverse is the transpose
    const [sx, sy] = inverse ? [ma * qx + mb * qy, mc * qx + md * qy] : [ma * qx + mc * qy, mb * qx + md * qy];
    return [Math.round(sx + h - 0.5), Math.round(n - (sy + h) - 0.5)];
  });
}

// ------------------------------------------------------------------ selections

/** A selection: 1 per selected pixel. */
export type Mask = Uint8Array;

export function rectMask(w: number, h: number, x0: number, y0: number, x1: number, y1: number): Mask {
  const m = new Uint8Array(w * h);
  const ax = Math.max(0, Math.min(x0, x1)), bx = Math.min(w - 1, Math.max(x0, x1));
  const ay = Math.max(0, Math.min(y0, y1)), by = Math.min(h - 1, Math.max(y0, y1));
  for (let y = ay; y <= by; y++) for (let x = ax; x <= bx; x++) m[y * w + x] = 1;
  return m;
}

/** Pixels whose centre is inside the lasso polygon (points in pixel units). */
export function lassoMask(w: number, h: number, pts: [number, number][]): Mask {
  const m = new Uint8Array(w * h);
  if (pts.length < 3) return m;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const px = x + 0.5, py = y + 0.5;
      let inside = false;
      for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        const [xi, yi] = pts[i], [xj, yj] = pts[j];
        if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
      }
      if (inside) m[y * w + x] = 1;
    }
  return m;
}

export function maskBounds(m: Mask, w: number): [number, number, number, number] | null {
  let x0 = Infinity, y0 = Infinity, x1 = -1, y1 = -1;
  for (let i = 0; i < m.length; i++)
    if (m[i]) {
      const x = i % w, y = Math.floor(i / w);
      x0 = Math.min(x0, x);
      y0 = Math.min(y0, y);
      x1 = Math.max(x1, x);
      y1 = Math.max(y1, y);
    }
  return x1 < 0 ? null : [x0, y0, x1, y1];
}

/** Floating pixels (a pasted or lifted piece) at an offset over the canvas. */
export interface Floating {
  img: Img;
  x: number;
  y: number;
}

/** The selected pixels as a piece of their own (unselected pixels transparent). */
export function extract(a: Img, m: Mask): Floating | null {
  const b = maskBounds(m, a.w);
  if (!b) return null;
  const [x0, y0, x1, y1] = b;
  const o = blank(x1 - x0 + 1, y1 - y0 + 1);
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) if (m[y * a.w + x]) setPx(o, x - x0, y - y0, getPx(a, x, y));
  return { img: o, x: x0, y: y0 };
}

export function clearMask(a: Img, m: Mask): Img {
  const o = cloneImg(a);
  for (let i = 0; i < m.length; i++) if (m[i]) setPx(o, i % a.w, Math.floor(i / a.w), CLEAR);
  return o;
}

/** Stamp a floating piece: its opaque pixels replace the canvas pixels under them. */
export function stamp(a: Img, f: Floating): Img {
  const o = cloneImg(a);
  for (let y = 0; y < f.img.h; y++)
    for (let x = 0; x < f.img.w; x++) {
      const c = getPx(f.img, x, y);
      if (c) setPx(o, f.x + x, f.y + y, c);
    }
  return o;
}

/** Copy a rectangle (x, y from the top) out of a bigger image. */
export function crop(a: Img, x: number, y: number, w: number, h: number): Img {
  const o = blank(w, h);
  for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) setPx(o, xx, yy, getPx(a, x + xx, y + yy));
  return o;
}

// ------------------------------------------------------------------ palette swap

/**
 * Colour mapping learned from image pairs with the same layout (a tile and its colour variant):
 * every colour of `from` goes to the colour it most often sits under in `to`.
 */
export function learnSwap(pairs: [Img, Img][]): Map<number, number> {
  const votes = new Map<number, Map<number, number>>();
  for (const [a, b] of pairs) {
    if (a.w !== b.w || a.h !== b.h) continue;
    for (let y = 0; y < a.h; y++)
      for (let x = 0; x < a.w; x++) {
        const ca = getPx(a, x, y), cb = getPx(b, x, y);
        if (!ca || !cb) continue;
        let v = votes.get(ca);
        if (!v) votes.set(ca, (v = new Map()));
        v.set(cb, (v.get(cb) ?? 0) + 1);
      }
  }
  const out = new Map<number, number>();
  for (const [c, v] of votes) {
    let best = c, bn = -1;
    for (const [t, n] of v) if (n > bn) [bn, best] = [n, t];
    out.set(c, best);
  }
  return out;
}

/** Recolour with a mapping; colours it doesn't know take the mapping of their nearest known colour. */
export function applySwap(a: Img, map: Map<number, number>): Img {
  const keys = [...map.keys()];
  const memo = new Map<number, number>();
  return mapPixels(a, (c) => {
    if (!c) return c;
    let t = memo.get(c);
    if (t === undefined) {
      t = map.get(c) ?? (keys.length ? map.get(nearestColor(c, keys))! : c);
      // keep the source alpha (the mapping is learned on colours)
      t = ((t & 0xffffff00) | (c & 255)) >>> 0;
      memo.set(c, t);
    }
    return t;
  });
}

export function mapPixels(a: Img, fn: (c: number) => number): Img {
  const o = blank(a.w, a.h);
  for (let y = 0; y < a.h; y++) for (let x = 0; x < a.w; x++) setPx(o, x, y, fn(getPx(a, x, y)));
  return o;
}

// ------------------------------------------------------------------ encoding

export function encodePx(a: Img): string {
  let s = '';
  for (let i = 0; i < a.d.length; i++) s += String.fromCharCode(a.d[i]);
  return btoa(s);
}

export function decodePx(s: string, w: number, h = w): Img {
  const o = blank(w, h);
  try {
    const b = atob(s);
    for (let i = 0; i < Math.min(b.length, o.d.length); i++) o.d[i] = b.charCodeAt(i);
  } catch {
    /* corrupt: blank */
  }
  return o;
}
